import React from "react";
import styled, { css } from "styled-components";
import { getStepLabel } from "../../../views/SubscriptionProjectV2/config/projectConfig";

/**
 * Compact horizontal stepper.
 * Circles connected by lines; labels underneath each.
 *
 * States per step:
 *  - completed : green ✓
 *  - current   : indigo accent, number visible
 *  - upcoming  : muted, number visible
 */
function HorizontalStepper({ steps = [], currentStep, lang = "th" }) {
  const currentIndex = steps.indexOf(currentStep);

  return (
    <Wrapper>
      <SectionLabel>{lang === "en" ? "PROGRESS" : "ขั้นตอน"}</SectionLabel>

      <Track>
        {steps.map((stepId, i) => {
          const isCompleted = i < currentIndex;
          const isCurrent = i === currentIndex;
          const status = isCompleted ? "completed" : isCurrent ? "current" : "upcoming";

          return (
            <React.Fragment key={stepId}>
              <Stop>
                <Circle $status={status} aria-current={isCurrent ? "step" : undefined}>
                  {isCompleted ? "✓" : i + 1}
                </Circle>
                <Label $status={status}>{getStepLabel(stepId, lang)}</Label>
              </Stop>

              {i < steps.length - 1 && <Line $completed={isCompleted} />}
            </React.Fragment>
          );
        })}
      </Track>
    </Wrapper>
  );
}

export default React.memo(HorizontalStepper);

// ─── Styled ──────────────────────────────────

const Wrapper = styled.div`
  padding: 12px 16px 14px;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Track = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Stop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  min-width: 60px;
`;

const circleStyles = {
  completed: css`
    background: #10b981;
    color: #ffffff;
  `,
  current: css`
    background: #6366f1;
    color: #ffffff;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
  `,
  upcoming: css`
    background: #f3f4f6;
    color: #9ca3af;
  `,
};

const Circle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  transition: all 0.2s;
  ${({ $status }) => circleStyles[$status]}
`;

const labelStyles = {
  completed: css`
    color: #6b7280;
  `,
  current: css`
    color: #111827;
    font-weight: 600;
  `,
  upcoming: css`
    color: #9ca3af;
  `,
};

const Label = styled.div`
  font-size: 10px;
  margin-top: 6px;
  text-align: center;
  line-height: 1.2;
  ${({ $status }) => labelStyles[$status]}
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  margin-top: 12px;
  background: ${({ $completed }) => ($completed ? "#10b981" : "#e5e7eb")};
  opacity: ${({ $completed }) => ($completed ? 0.5 : 1)};
`;